import axios, { AxiosError, AxiosInstance } from 'axios'

const API_URL = '/api'

export interface ApiError {
  error: string
  details?: string
}

export interface AnalyticsSeriesPoint {
  timestamp: string
  sessions: number
  viewers: number
  watch_hours: number
  errors: number
  buffers: number
}

export interface AnalyticsOverview {
  active_viewers: number
  unique_viewers: number
  sessions: number
  watch_hours: number
  peak_concurrency: number
  avg_startup_time_ms: number
  p50_startup_time_ms: number
  p95_startup_time_ms: number
  buffering_ratio: number
  buffered_session_rate: number
  error_rate: number
  startup_failure_rate: number
  recovery_rate: number
  crash_free_rate: number
  series: AnalyticsSeriesPoint[]
}

export interface AnalyticsSession {
  id: string
  external_session_id: string
  username?: string
  channel_name?: string
  device_model?: string
  app?: string
  app_version?: string
  started_at: string
  ended_at?: string
  end_reason?: string
  startup_time_ms?: number
  buffered_time_ms: number
  buffer_count: number
  retry_count: number
  error_count: number
  source?: 'obacast' | 'emby'
  remote_endpoint?: string
  playback_method?: string
  paused?: boolean
  position_ms?: number
  video_codec?: string
  audio_codec?: string
  resolution?: string
  bitrate?: number
  container?: string
  transcode_reasons?: string[]
  duration_seconds?: number
}

export interface SourceReliabilityRow {
  stream_id: string
  stream_name: string
  source_index: number
  source_domain: string
  playback_profile?: string
  starts: number
  failures: number
  failovers: number
  affected_viewers: number
  success_rate: number
  last_failure_at?: string
  last_failure_reason?: string
  last_active: string
}

export interface AnalyticsSessionList {
  items: AnalyticsSession[]
  total: number
  limit: number
  offset: number
  source_counts?: { obacast: number; emby: number }
  emby_configured?: boolean
  emby_error?: string
}

export interface AnalyticsSessionEvent {
  id: string
  event_type: string
  occurred_at: string
  duration_ms?: number
  error_code?: string
  error_message?: string
  metadata?: Record<string, unknown>
}

export interface AnalyticsSessionDetail {
  session: AnalyticsSession
  events: AnalyticsSessionEvent[]
}

export interface AnalyticsDimensionRow {
  key: string
  label: string
  sessions: number
  unique_viewers: number
  watch_hours: number
  avg_session_minutes: number
  avg_startup_time_ms: number
  buffering_ratio: number
  buffered_session_rate: number
  buffer_events: number
  retry_events: number
  error_events: number
  error_rate: number
  last_active: string
}

// Debug API Types
export interface DebugConfig {
  enabled: boolean
  max_log_lines: number
  allow_search: boolean
  rate_limit: number
}

export interface StreamLogEntry {
  stream_id: string
  stream_name: string
  state: string
  logs: string[]
  log_count: number
  truncated?: boolean
}

export interface DebugLogsResponse {
  total_streams: number
  config: DebugConfig
  streams: StreamLogEntry[]
}

export interface SearchMatch {
  stream_id: string
  stream_name: string
  matches: string[]
  match_count: number
}

export interface DebugSearchResponse {
  query: string
  total_matches: number
  streams: SearchMatch[]
}

export interface StreamTechInfo {
  video_codec?: string
  audio_codec?: string
  resolution?: string
  fps?: string
  bitrate?: string
}

export interface DebugStreamDiagnostics {
  stream_id: string
  stream_name: string
  state: string
  input_url?: string
  output_path?: string
  started_at?: string
  last_activity?: string
  restart_count: number
  tech_info?: StreamTechInfo
  logs: string[]
  log_count: number
  truncated?: boolean
}

export interface DebugHealth {
  instance?: string
  total_active: number
  running: number
  starting?: number
  error?: number
  producing?: number
  healthy: boolean
}

export interface FFmpegProcess {
  id: string
  stream_id: string
  pid: number
  state: string
  input_url?: string
  output_path?: string
  started_at?: string
  last_activity?: string
  restart_count?: number
  bitrate?: string
  speed?: string
  frame?: number
  video_codec?: string
  audio_codec?: string
  resolution?: string
  fps?: string
}

export interface DebugFFmpegProcesses {
  count: number
  processes: FFmpegProcess[]
}

class ApiClient {
  private client: AxiosInstance
  private accessToken: string | null = null

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use((config) => {
      const token = this.accessToken || localStorage.getItem('access_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // Response interceptor for token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config
        if (error.response?.status === 401 && originalRequest) {
          try {
            const refreshToken = localStorage.getItem('refresh_token') || localStorage.getItem('watch_refresh_token')
            if (refreshToken) {
              const { data } = await axios.post(`${API_URL}/auth/refresh`, {
                refresh_token: refreshToken,
              })
              this.setTokens(data.access_token, data.refresh_token)
              originalRequest.headers.Authorization = `Bearer ${data.access_token}`
              return this.client(originalRequest)
            }
          } catch (refreshError) {
            this.clearTokens()
            const isWatch = window.location.hostname.startsWith('watch.')
            window.location.href = isWatch ? '/login' : '/login'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken
    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('refresh_token', refreshToken)
  }

  clearTokens() {
    this.accessToken = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  hasToken(): boolean {
    return !!localStorage.getItem('access_token')
  }

  // Auth
  async login(username: string, password: string) {
    const { data } = await this.client.post('/auth/login', { username, password })
    this.setTokens(data.access_token, data.refresh_token)
    return data
  }

  async logout() {
    try {
      await this.client.post('/auth/logout')
    } finally {
      this.clearTokens()
    }
  }

  // Web Player Auth (streaming user login)
  // Note: Does NOT auto-store tokens - let useWatchAuth handle storage
  async userLogin(username: string, password: string): Promise<UserLoginResponse> {
    const { data } = await this.client.post('/auth/user-login', { username, password })
    return data
  }

  // Web Player Channels
  async getEPGGuide(start?: string, end?: string, channels?: string[]): Promise<EPGGuideResponse> {
    const params = new URLSearchParams()
    if (start) params.set('start', start)
    if (end) params.set('end', end)
    if (channels?.length) params.set('channels', channels.join(','))
    const { data } = await this.client.get('/epg/guide?' + params.toString())
    return data
  }

  async getAccountFeatures(): Promise<AccountFeatures> {
    const { data } = await this.client.get('/account/features')
    return data
  }

  async getWebPlayerChannels(): Promise<WebPlayerChannel[]> {
    const { data } = await this.client.get('/watch/channels')
    return data.channels || []
  }

  // Stop playback - notify server that client has stopped watching
  // streamUrl format: /play/{username}/{password}/{streamID}.m3u8
  async stopPlayback(streamUrl: string): Promise<void> {
    // Convert stream URL to stop URL
    // /play/user/pass/streamID.m3u8 -> /play/user/pass/streamID/stop
    const stopUrl = streamUrl.replace('.m3u8', '/stop')
    try {
      await this.client.post(stopUrl)
    } catch (error) {
      // Don't throw - stopping is best-effort
      console.warn('[API] Failed to notify server of playback stop:', error)
    }
  }

  // Cast Session API - for multiview casting to Chromecast/AirPlay
  async createCastSession(req: CreateCastSessionRequest): Promise<CastSession> {
    const { data } = await this.client.post('/cast/sessions', req)
    return data
  }

  async getCastSession(id: string): Promise<CastSession> {
    const { data } = await this.client.get(`/cast/sessions/${id}`)
    return data
  }

  async updateCastSession(id: string, req: UpdateCastSessionRequest): Promise<CastSession> {
    const { data } = await this.client.put(`/cast/sessions/${id}`, req)
    return data
  }

  async deleteCastSession(id: string): Promise<void> {
    await this.client.delete(`/cast/sessions/${id}`)
  }

  async changePassword(currentPassword: string, newPassword: string) {
    const { data } = await this.client.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
    return data
  }

  // Dashboard
  async getDashboardStats() {
    const { data } = await this.client.get('/dashboard/stats')
    return data
  }

  async getActiveStreams() {
    const { data } = await this.client.get('/dashboard/streams')
    return data
  }

  async getActiveSessions() {
    const { data } = await this.client.get('/dashboard/sessions')
    return data
  }

  async getRecentEvents() {
    const { data } = await this.client.get('/dashboard/events')
    return data
  }

  async getAnalyticsOverview(params: { from: string; to: string }): Promise<AnalyticsOverview> {
    const { data } = await this.client.get('/admin/analytics/overview', { params })
    return data
  }

  async getAnalyticsLive(): Promise<AnalyticsSessionList> {
    const { data } = await this.client.get('/admin/analytics/live')
    return data
  }

  async getAnalyticsHistory(params: Record<string, string | number>): Promise<AnalyticsSessionList> {
    const { data } = await this.client.get('/admin/analytics/history', { params })
    return data
  }

  async getAnalyticsSession(id: string): Promise<AnalyticsSessionDetail> {
    const { data } = await this.client.get(`/admin/analytics/sessions/${id}`)
    return data
  }

  async getAnalyticsDimension(dimension: string, params: { from: string; to: string }): Promise<{ items: AnalyticsDimensionRow[] }> {
    const { data } = await this.client.get(`/admin/analytics/insights/${dimension}`, { params })
    return data
  }

  async getSourceReliability(params: { from: string; to: string }): Promise<{ items: SourceReliabilityRow[] }> {
    const { data } = await this.client.get('/admin/analytics/source-reliability', { params })
    return data
  }

  async downloadAnalyticsHistory(params: Record<string, string | number>): Promise<void> {
    const response = await this.client.get('/admin/analytics/history.csv', { params, responseType: 'blob' })
    const url = URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = 'obacast-playback-history.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  // Recordings — used by the Dashboard's recording tiles. Lists all
  // recordings (paginated; admin sees everyone's, regular users see
  // their own) and storage info (used / quota / count).
  async getRecordings(params?: { status?: string; per_page?: number; page?: number; mine?: string; user?: string }) {
    const { data } = await this.client.get('/recordings', { params })
    return data
  }

  async getRecordingStorage() {
    const { data } = await this.client.get('/recordings/storage')
    return data
  }

  async deleteRecording(id: string) {
    const { data } = await this.client.delete(`/recordings/${id}`)
    return data
  }

  async getSeriesRules() {
    const { data } = await this.client.get('/recordings/series')
    return data
  }

  // Users
  async getUsers(params?: { page?: number; per_page?: number; search?: string }) {
    const { data } = await this.client.get('/users', { params })
    return data
  }

  async getUser(id: string) {
    const { data } = await this.client.get(`/users/${id}`)
    return data
  }

  // OBACast featured (hero) channel pin
  async getFeaturedChannel(): Promise<{ featured_channel_id: string | null }> {
    const { data } = await this.client.get('/admin/featured-channel')
    return data
  }

  async setFeaturedChannel(id: string | null) {
    const { data } = await this.client.put('/admin/featured-channel', { featured_channel_id: id })
    return data
  }

  async createUser(user: CreateUserRequest) {
    const { data } = await this.client.post('/users', user)
    return data
  }

  async updateUser(id: string, user: UpdateUserRequest) {
    const { data } = await this.client.put(`/users/${id}`, user)
    return data
  }

  async deleteUser(id: string) {
    await this.client.delete(`/users/${id}`)
  }

  // Streams
  async getStreams(params?: {
    page?: number
    per_page?: number
    type?: string
    search?: string
    category_id?: string
    is_active?: string  // 'true', 'false', or empty for all
    is_online?: string  // 'true' to filter only online streams
  }) {
    const { data } = await this.client.get('/streams', { params })
    return data
  }

  async getStream(id: string) {
    const { data } = await this.client.get(`/streams/${id}`)
    return data
  }

  async createStream(stream: CreateStreamRequest) {
    const { data } = await this.client.post('/streams', stream)
    return data
  }

  async updateStream(id: string, stream: UpdateStreamRequest) {
    const { data } = await this.client.put(`/streams/${id}`, stream)
    return data
  }

  async deleteStream(id: string) {
    await this.client.delete(`/streams/${id}`)
  }

  async batchUpdateStreams(streamIds: string[], updates: {
    is_active?: boolean
    category_id?: string | null
    custom_user_agent?: string
  }): Promise<{ status: string; updated: number }> {
    const { data } = await this.client.put('/streams/batch', {
      stream_ids: streamIds,
      updates: {
        is_active: updates.is_active,
        category_id: updates.category_id === null ? '' : updates.category_id,
        custom_user_agent: updates.custom_user_agent,
      },
    })
    return data
  }

  async getStreamStats(): Promise<{ total: number; by_type: Record<string, number>; unassigned: number }> {
    const { data } = await this.client.get('/streams/stats')
    return data
  }

  // Categories
  async getCategories(params?: { type?: string }) {
    const { data } = await this.client.get('/categories', { params })
    return data
  }

  async createCategory(category: CreateCategoryRequest) {
    const { data } = await this.client.post('/categories', category)
    return data
  }

  async updateCategory(id: string, category: UpdateCategoryRequest) {
    const { data } = await this.client.put(`/categories/${id}`, category)
    return data
  }

  async deleteCategory(id: string) {
    await this.client.delete(`/categories/${id}`)
  }

  async renumberCategory(id: string) {
    const { data } = await this.client.post(`/categories/${id}/renumber`)
    return data
  }

  async assignChannelNumbers(): Promise<AssignChannelNumbersResponse> {
    const { data } = await this.client.post('/categories/assign-numbers')
    return data
  }

  async getCategoryRanges() {
    const { data } = await this.client.get('/categories/ranges')
    return data
  }

  async validateCategoryRange(rangeStart: number, rangeEnd: number, categoryId?: string) {
    const { data } = await this.client.post('/categories/validate-range', {
      range_start: rangeStart,
      range_end: rangeEnd,
      category_id: categoryId,
    })
    return data
  }

  // Bouquets
  async getBouquets(params?: { page?: number; per_page?: number }) {
    const { data } = await this.client.get('/bouquets', { params })
    return data
  }

  async getBouquet(id: string) {
    const { data } = await this.client.get(`/bouquets/${id}`)
    return data
  }

  async createBouquet(bouquet: CreateBouquetRequest) {
    const { data } = await this.client.post('/bouquets', bouquet)
    return data
  }

  async updateBouquet(id: string, bouquet: UpdateBouquetRequest) {
    const { data } = await this.client.put(`/bouquets/${id}`, bouquet)
    return data
  }

  async deleteBouquet(id: string) {
    await this.client.delete(`/bouquets/${id}`)
  }

  // Series
  async getSeries(params?: { page?: number; per_page?: number; search?: string }) {
    const { data } = await this.client.get('/series', { params })
    return data
  }

  async getSeriesById(id: string) {
    const { data } = await this.client.get(`/series/${id}`)
    return data
  }

  // EPG
  async getEPGSources() {
    const { data } = await this.client.get('/epg/sources')
    return data
  }

  async createEPGSource(source: CreateEPGSourceRequest) {
    const { data } = await this.client.post('/epg/sources', source)
    return data
  }

  async syncEPGSource(id: string) {
    const { data } = await this.client.post(`/epg/sources/${id}/sync`)
    return data
  }

  async deleteEPGSource(id: string) {
    await this.client.delete(`/epg/sources/${id}`)
  }

  async updateEPGSource(id: string, source: { name: string; url: string; type?: string; is_active?: boolean; sync_interval_hours?: number; sync_time?: string | null }) {
    const { data } = await this.client.put(`/epg/sources/${id}`, source)
    return data
  }

  async getEPGChannels() {
    const { data } = await this.client.get('/epg/channels')
    return data
  }

  async getEPGSyncStatus(): Promise<EPGSyncStatusResponse> {
    const { data } = await this.client.get('/epg/sync-status')
    return data
  }

  // Stream testing
  async testStream(id: string) {
    const { data } = await this.client.post(`/streams/${id}/test`)
    return data
  }

  async getStreamLogs(id: string) {
    const { data } = await this.client.get(`/streams/${id}/logs`)
    return data
  }

  async stopTestStream(id: string) {
    const { data } = await this.client.post(`/streams/${id}/stop`)
    return data
  }

  // Stream probing
  async probeURL(url: string) {
    const { data } = await this.client.post('/streams/probe', { url })
    return data
  }

  async probeStream(id: string) {
    const { data } = await this.client.post(`/streams/${id}/probe`)
    return data
  }

  async getChannelHealthSnapshot(id: string): Promise<unknown> {
    const { data } = await this.client.get(`/diagnostics/streams/${id}/health`)
    return data
  }

  async getStreamReliability(id: string): Promise<SourceReliabilityReport> {
    const { data } = await this.client.get(`/streams/${id}/reliability`)
    return data
  }

  async runReliabilityCheck(
    id: string,
    request: { source_index: number; check_type: ReliabilityCheckType; allow_expensive?: boolean }
  ): Promise<ReliabilityCheckResponse> {
    const { data } = await this.client.post(`/streams/${id}/reliability/check`, request)
    return data
  }

  async markSourceDegraded(id: string, sourceIndex: number, reason?: string) {
    const { data } = await this.client.post(`/streams/${id}/reliability/source/${sourceIndex}/degrade`, { reason })
    return data
  }

  async resetSourceHealth(id: string, sourceIndex: number) {
    const { data } = await this.client.post(`/streams/${id}/reliability/source/${sourceIndex}/reset`)
    return data
  }

  // Stream failover
  async forceFailover(id: string, request?: { source_index?: number; override_cooldown?: boolean }) {
    const { data } = await this.client.post(`/streams/${id}/failover`, request ?? {})
    return data
  }

  async updateSourcePriority(id: string, priorities: number[]) {
    const { data } = await this.client.put(`/streams/${id}/sources/priority`, priorities)
    return data
  }

  // Settings
  async getSettings() {
    const { data } = await this.client.get('/settings')
    return data
  }

  async getSetting(key: string) {
    const { data } = await this.client.get(`/settings/${key}`)
    return data
  }

  async setSetting(key: string, value: any) {
    const { data } = await this.client.put(`/settings/${key}`, { value })
    return data
  }

  async setSettings(settings: Record<string, any>) {
    const { data } = await this.client.put('/settings', settings)
    return data
  }

  async getClientVersionPolicy() {
    const { data } = await this.client.get('/settings/client-version-policy')
    return data
  }

  async updateClientVersionPolicy(policy: Record<string, any>) {
    const { data } = await this.client.put('/settings/client-version-policy', policy)
    return data
  }

  async rollbackClientVersionPolicy(reason: string) {
    const { data } = await this.client.post('/settings/client-version-policy/rollback', { reason })
    return data
  }

  async getDefaultUserAgent() {
    const { data } = await this.client.get('/settings/user-agent')
    return data
  }

  async setDefaultUserAgent(userAgent: string) {
    const { data } = await this.client.put('/settings/user-agent', { user_agent: userAgent })
    return data
  }

  // Broadcast Messages (Emby notifications)
  async getBroadcastConfig(): Promise<BroadcastConfig> {
    const { data } = await this.client.get('/broadcast/config')
    return data
  }

  async setBroadcastConfig(config: BroadcastConfigRequest): Promise<void> {
    await this.client.post('/broadcast/config', config)
  }

  async testBroadcastConnection(): Promise<BroadcastTestResponse> {
    const { data } = await this.client.post('/broadcast/test')
    return data
  }

  async sendBroadcast(message: BroadcastSendRequest): Promise<BroadcastSendResponse> {
    const { data } = await this.client.post('/broadcast/send', message)
    return data
  }

  async getBroadcastHistory(): Promise<BroadcastMessage[]> {
    const { data } = await this.client.get('/broadcast/history')
    return data
  }

  async deleteBroadcastMessage(id: string): Promise<void> {
    await this.client.delete(`/broadcast/history/${id}`)
  }

  async clearBroadcastHistory(): Promise<void> {
    await this.client.delete('/broadcast/history')
  }

  // Transcode Profiles
  async getTranscodeProfiles() {
    const { data } = await this.client.get('/transcode/profiles')
    return data
  }

  async getTranscodeProfile(id: string) {
    const { data } = await this.client.get(`/transcode/profiles/${id}`)
    return data
  }

  async createTranscodeProfile(profile: CreateTranscodeProfileRequest) {
    const { data } = await this.client.post('/transcode/profiles', profile)
    return data
  }

  async updateTranscodeProfile(id: string, profile: UpdateTranscodeProfileRequest) {
    const { data } = await this.client.put(`/transcode/profiles/${id}`, profile)
    return data
  }

  async deleteTranscodeProfile(id: string) {
    await this.client.delete(`/transcode/profiles/${id}`)
  }

  async getTranscodePresets() {
    const { data } = await this.client.get('/transcode/presets')
    return data
  }

  async previewTranscodeArgs(profile: CreateTranscodeProfileRequest) {
    const { data } = await this.client.post('/transcode/preview-args', profile)
    return data
  }

  // Sports EPG
  async getSportsSettings() {
    const { data } = await this.client.get('/sports/settings')
    return data
  }

  async updateSportsSettings(settings: UpdateSportsSettingsRequest) {
    const { data } = await this.client.put('/sports/settings', settings)
    return data
  }

  async testSportsPanelConnection(url: string, username: string, password: string) {
    const { data } = await this.client.post('/sports/settings/test-panel', { url, username, password })
    return data
  }

  async testSportsEmbyConnection(url: string, apiKey: string) {
    const { data } = await this.client.post('/sports/settings/test-emby', { url, api_key: apiKey })
    return data
  }

  async getSportsStatus() {
    const { data } = await this.client.get('/sports/status')
    return data
  }

  async triggerSportsRefresh(leagueCode?: string) {
    const params = leagueCode ? { league: leagueCode } : {}
    const { data } = await this.client.post('/sports/refresh', null, { params })
    return data
  }

  async getSportsLeagues() {
    const { data } = await this.client.get('/sports/leagues')
    return data
  }

  async getSportsLeague(code: string) {
    const { data } = await this.client.get(`/sports/leagues/${code}`)
    return data
  }

  async updateSportsLeague(code: string, update: UpdateSportsLeagueRequest) {
    const { data } = await this.client.put(`/sports/leagues/${code}`, update)
    return data
  }

  async getStadiumCategories(): Promise<StadiumCategoryConfig[]> {
    const { data } = await this.client.get('/sports/stadium/categories')
    return data
  }

  async updateStadiumCategory(code: string, update: UpdateStadiumCategoryRequest): Promise<StadiumCategoryConfig[]> {
    const { data } = await this.client.put(`/sports/stadium/categories/${code}`, update)
    return data
  }

  async getSportsTeams(leagueCode: string) {
    const { data } = await this.client.get(`/sports/leagues/${leagueCode}/teams`)
    return data
  }

  async getSportsTeam(id: string) {
    const { data } = await this.client.get(`/sports/teams/${id}`)
    return data
  }

  async updateSportsTeam(id: string, update: UpdateSportsTeamRequest) {
    const { data } = await this.client.put(`/sports/teams/${id}`, update)
    return data
  }

  async getSportsChannelMappings() {
    const { data } = await this.client.get('/sports/mappings')
    return data
  }

  async createSportsChannelMapping(mapping: CreateSportsChannelMappingRequest) {
    const { data } = await this.client.post('/sports/mappings', mapping)
    return data
  }

  async updateSportsChannelMapping(id: string, update: UpdateSportsChannelMappingRequest) {
    const { data } = await this.client.put(`/sports/mappings/${id}`, update)
    return data
  }

  async deleteSportsChannelMapping(id: string) {
    await this.client.delete(`/sports/mappings/${id}`)
  }

  async getSportsGames(leagueCode?: string) {
    const params = leagueCode ? { league: leagueCode } : {}
    const { data } = await this.client.get('/sports/games', { params })
    return data
  }

  async getSportsLiveGames() {
    const { data } = await this.client.get('/sports/games/live')
    return data
  }

  async getSportsEPGRuns() {
    const { data } = await this.client.get('/sports/runs')
    return data
  }

  // Generic Channels (NFL-style)
  async getSportsGenericChannels(leagueCode: string) {
    const { data } = await this.client.get(`/sports/leagues/${leagueCode}/channels`)
    return data as SportsGenericChannel[]
  }

  async getSportsGenericChannel(id: string) {
    const { data } = await this.client.get(`/sports/channels/${id}`)
    return data as SportsGenericChannel
  }

  async updateSportsGenericChannel(id: string, update: UpdateSportsGenericChannelRequest) {
    const { data } = await this.client.put(`/sports/channels/${id}`, update)
    return data as SportsGenericChannel
  }

  async mapSportsGenericChannelStream(id: string, mapping: MapGenericChannelRequest) {
    const { data } = await this.client.post(`/sports/channels/${id}/map-stream`, mapping)
    return data as SportsGenericChannel
  }

  async getSportsChannelGames(channelId: string) {
    const { data } = await this.client.get(`/sports/channels/${channelId}/games`)
    return data
  }

  // Panel Sources (multi-source support)
  async getPanelSources(): Promise<SportsPanelSource[]> {
    const { data } = await this.client.get('/sports/sources')
    return data
  }

  async getPanelSource(id: string): Promise<SportsPanelSource> {
    const { data } = await this.client.get(`/sports/sources/${id}`)
    return data
  }

  async createPanelSource(source: CreatePanelSourceRequest): Promise<SportsPanelSource> {
    const { data } = await this.client.post('/sports/sources', source)
    return data
  }

  async updatePanelSource(id: string, source: UpdatePanelSourceRequest): Promise<SportsPanelSource> {
    const { data } = await this.client.put(`/sports/sources/${id}`, source)
    return data
  }

  async deletePanelSource(id: string): Promise<void> {
    await this.client.delete(`/sports/sources/${id}`)
  }

  async testPanelSourceConnection(id: string): Promise<PanelSourceTestResult> {
    const { data } = await this.client.post(`/sports/sources/${id}/test`)
    return data
  }

  async setDefaultPanelSource(id: string): Promise<{ message: string }> {
    const { data } = await this.client.post(`/sports/sources/${id}/set-default`)
    return data
  }

  async refreshPanelSource(id: string): Promise<{ success: boolean; message?: string; error?: string }> {
    const { data } = await this.client.post(`/sports/sources/${id}/refresh`)
    return data
  }

  // XUI Import
  async getXUICategories(panelCreds?: { panel_url?: string; panel_username?: string; panel_password?: string }): Promise<XUICategory[]> {
    const { data } = await this.client.get('/import/xui/categories', { params: panelCreds })
    return data
  }

  async getXUIStreams(params?: { category_id?: string; search?: string; panel_url?: string; panel_username?: string; panel_password?: string }): Promise<XUIStreamListResponse> {
    const { data } = await this.client.get('/import/xui/streams', { params })
    return data
  }

  async importXUIStreams(request: ImportStreamsRequest): Promise<ImportStreamsResponse> {
    const { data } = await this.client.post('/import/xui/streams', request)
    return data
  }

  async previewM3UStreams(file: File): Promise<M3UStreamListResponse> {
    const formData = new FormData()
    formData.append('m3u', file)
    const { data } = await this.client.post('/import/m3u/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  }

  async importM3UStreams(request: ImportM3UStreamsRequest): Promise<ImportStreamsResponse> {
    const formData = new FormData()
    formData.append('m3u', request.file)
    request.stream_ids.forEach((id) => formData.append('stream_ids', id))
    if (request.category_id) formData.append('category_id', request.category_id)
    if (request.create_category !== undefined) formData.append('create_category', String(request.create_category))
    const { data } = await this.client.post('/import/m3u/streams', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  }

  // Debug API
  async getDebugConfig(): Promise<DebugConfig> {
    const { data } = await this.client.get('/debug/config')
    return data
  }

  async updateDebugConfig(config: DebugConfig): Promise<DebugConfig> {
    const { data } = await this.client.put('/debug/config', config)
    return data
  }

  async getDebugLogs(): Promise<DebugLogsResponse> {
    const { data } = await this.client.get('/debug/logs')
    return data
  }

  async searchDebugLogs(query: string): Promise<DebugSearchResponse> {
    const { data } = await this.client.get('/debug/logs/search', { params: { q: query } })
    return data
  }

  async getDebugStreamDiagnostics(streamId: string): Promise<DebugStreamDiagnostics> {
    const { data } = await this.client.get(`/debug/streams/${streamId}`)
    return data
  }

  async getDebugHealth(): Promise<DebugHealth> {
    const { data } = await this.client.get('/debug/health')
    return data
  }

  async getDebugFFmpegProcesses(): Promise<DebugFFmpegProcesses> {
    const { data } = await this.client.get('/debug/ffmpeg')
    return data
  }

  getAccessToken(): string | null {
    return localStorage.getItem('access_token')
  }

}

export const api = new ApiClient()

// Request/Response types
export interface CreateUserRequest {
  username: string
  password: string
  max_connections?: number
  expires_at?: string
  is_active?: boolean
  bouquet_ids?: string[]
  web_player_enabled?: boolean
  mosaic_enabled?: boolean
  recording_enabled?: boolean
  max_mosaic_streams?: number
}

export interface UpdateUserRequest {
  password?: string
  max_connections?: number
  expires_at?: string
  is_active?: boolean
  bouquet_ids?: string[]
  web_player_enabled?: boolean
  mosaic_enabled?: boolean
  recording_enabled?: boolean
  max_mosaic_streams?: number
}

// Web Player types
export interface EPGProgram {
  id: string
  title: string
  description?: string
  start_time: string
  end_time: string
  category?: string
  icon?: string
  episode?: string
}

export interface EPGChannelGuide {
  channel_id: string
  name: string
  icon_url?: string
  programs: EPGProgram[]
}

export interface EPGGuideResponse {
  channels: EPGChannelGuide[]
  start: string
  end: string
}

export interface AccountFeatures {
  mosaic: boolean
  recording: boolean
  max_streams: number
  max_mosaic_streams: number
  web_player: boolean
  is_admin: boolean
}

export interface WebPlayerUser {
  id: string
  username: string
  web_player_enabled: boolean
  mosaic_enabled: boolean
  max_mosaic_streams: number
  recording_enabled: boolean
  is_admin: boolean
}

export interface UserLoginResponse {
  access_token: string
  refresh_token: string
  user: WebPlayerUser
}

export interface WebPlayerChannel {
  id: string
  name: string
  stream_url: string
  icon_url?: string
  category?: string
  category_id?: string
  number?: number
}

// Cast Session types for multiview casting
export interface CastSessionTile {
  slot: number
  stream_id: string
}

export interface CreateCastSessionRequest {
  layout: string
  tiles: CastSessionTile[]
  active_audio_slot: number
}

export interface UpdateCastSessionRequest {
  layout?: string
  tiles?: CastSessionTile[]
  active_audio_slot?: number
}

export interface CastSession {
  id: string
  user_id: string
  layout: string
  tiles: CastSessionTile[]
  active_audio_slot: number
  playback_token: string
  playback_url: string
  state: string
  error_message?: string
  created_at: string
  updated_at: string
}

export interface CreateStreamRequest {
  name: string
  type: 'live' | 'movie' | 'series'
  category_id?: string
  sources: StreamSource[]
  icon_url?: string
  epg_channel_id?: string
  is_direct_source?: boolean
  is_active?: boolean
  fix_timestamps?: boolean
  startup_mode?: 'on_demand' | 'always_on'
  custom_user_agent?: string
  notes?: string
  channel_number?: number
}

export interface UpdateStreamRequest extends Partial<CreateStreamRequest> {}

export interface StreamSource {
  url: string
  priority: number
  name?: string
  playback_profile?:
    | 'auto'
    | 'direct_passthrough'
    | 'hls_proxy'
    | 'ffmpeg_relay'
    | 'timestamp_repair'
    | 'backup_first'
    | 'limited_connection'
    | 'do_not_probe'
  connection_group?: string
}

export type ReliabilityCheckType =
  | 'metadata_only'
  | 'manifest_only'
  | 'playlist_parse'
  | 'single_segment'
  | 'full_playback_probe'

export interface ChannelHealthLayer {
  state: string
  score: number | null
  last_observed?: string
  coverage_percent: number
  missing: string[]
  issues: Record<string, number>
  samples: number
}
export interface ChannelHealthReport {
  state: string
  score: number | null
  generated_at: string
  scope: string
  truncated: boolean
  layers: Record<string, ChannelHealthLayer>
}
export interface SourceReliabilityReport {
  evidence?: ChannelHealthReport
  stream_id: string
  stream_name: string
  is_active: boolean
  is_online: boolean
  state: string
  viewer_count: number
  current_source: number
  last_message?: string
  recovery_elapsed_seconds?: number
  sources: SourceReliabilitySource[]
  connection_groups?: ConnectionGroupReliability[]
}

export interface SourceReliabilitySource {
  id: string
  index: number
  name?: string
  domain: string
  priority: number
  playback_profile: StreamSource['playback_profile']
  effective_playback_profile: StreamSource['playback_profile']
  connection_group?: string
  health_state: 'unknown' | 'healthy' | 'warning' | 'degraded' | 'failed'
  health_score: number
  health_reason?: string
  is_current: boolean
  failure_count: number
  active_viewer_count: number
  limited_connection: boolean
  probe_allowed: boolean
  probe_warning?: string
  last_failure_reason?: string
  last_successful_playback?: string
  last_viewer_failure?: string
  viewer_buffer_count: number
  viewer_retry_count: number
  viewer_error_count: number
  recent_affected_viewers: number
  process_restart_count: number
}

export interface ConnectionGroupReliability {
  name: string
  source_count: number
  active_viewer_count: number
  limited: boolean
  probe_policy: string
}

export interface ReliabilityCheckResponse {
  success: boolean
  skipped: boolean
  check_type: ReliabilityCheckType
  stream_id: string
  source_index: number
  status_code?: number
  message: string
  reason?: string
  report?: SourceReliabilityReport
}

export interface CreateCategoryRequest {
  name: string
  type: 'live' | 'movie' | 'series'
  parent_id?: string
  icon_url?: string
  sort_order?: number
  is_active?: boolean
  channel_range_start?: number | null
  channel_range_end?: number | null
  stable_numbering?: boolean
  auto_assign_numbers?: boolean
}

export interface UpdateCategoryRequest extends Partial<CreateCategoryRequest> {}

export interface CreateBouquetRequest {
  name: string
  description?: string
  stream_ids?: string[]
  category_ids?: string[]
  is_active?: boolean
}

export interface UpdateBouquetRequest extends Partial<CreateBouquetRequest> {}

export interface CreateEPGSourceRequest {
  name: string
  url: string
  type?: string
  sync_interval_hours?: number
  sync_time?: string | null // "HH:MM" UTC for daily scheduled sync
}

export interface CreateTranscodeProfileRequest {
  name: string
  preset?: 'passthrough' | 'universal' | 'low_bandwidth' | 'audio_only' | 'custom'
  description?: string
  video_codec?: string
  video_width?: number
  video_height?: number
  video_bitrate?: number
  video_fps?: number
  video_profile?: string
  audio_codec?: string
  audio_bitrate?: number
  audio_sample_rate?: number
  audio_channels?: number
  custom_ffmpeg_args?: string
}

export interface UpdateTranscodeProfileRequest extends Partial<CreateTranscodeProfileRequest> {}

export interface TranscodeProfile {
  id: string
  name: string
  preset: string
  description?: string
  is_system: boolean
  video_codec?: string
  video_width?: number
  video_height?: number
  video_bitrate?: number
  video_fps?: number
  video_profile?: string
  audio_codec?: string
  audio_bitrate?: number
  audio_sample_rate?: number
  audio_channels?: number
  custom_ffmpeg_args?: string
  created_at: string
  updated_at: string
}

// Sports EPG types
export interface SportsSettings {
  emby_enabled: boolean
  emby_url: string
  emby_api_key: string
  panel_url: string
  panel_username: string
  panel_password: string
  epg_output_dir: string
  timezone: string
}

export interface UpdateSportsSettingsRequest {
  emby_enabled?: boolean
  emby_url?: string
  emby_api_key?: string
  panel_url?: string
  panel_username?: string
  panel_password?: string
  epg_output_dir?: string
  timezone?: string
}

export interface SportsLeague {
  id: string
  code: string
  name: string
  short_name: string
  espn_slug: string
  enabled: boolean
  panel_category?: string
  panel_source_id?: string
  panel_source?: SportsPanelSource
  timezone: string
  update_interval_seconds: number
  epg_refresh_hours: number
  game_duration_hours: number
  icon_url?: string
  last_epg_generation?: string
  last_score_update?: string
  use_generic_channels: boolean
  generic_channel_count: number
  created_at: string
  updated_at: string
  total_teams?: number
  enabled_teams?: number
  mapped_teams?: number
  today_games?: number
  live_games?: number
}

export interface UpdateSportsLeagueRequest {
  enabled?: boolean
  panel_category?: string
  panel_source_id?: string | null
  timezone?: string
  update_interval_seconds?: number
  epg_refresh_hours?: number
  use_generic_channels?: boolean
  generic_channel_count?: number
}

export interface StadiumCategoryConfig {
  code: string
  enabled: boolean
  display_name: string
  display_order: number
  availability_window_days: number
  updated_at: string
}

export interface UpdateStadiumCategoryRequest {
  enabled?: boolean
  display_name?: string
  display_order?: number
  availability_window_days?: number
}

export interface SportsTeam {
  id: string
  league_id: string
  espn_team_id?: string
  name: string
  display_name?: string
  abbreviation?: string
  city?: string
  mascot?: string
  search_terms: string[]
  exclude_terms: string[]
  logo_url?: string
  primary_color?: string
  secondary_color?: string
  conference?: string
  division?: string
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
  mapping_id?: string
  mapped_stream_id?: string
  mapped_stream_name?: string
  mapping_enabled: boolean
}

export interface UpdateSportsTeamRequest {
  is_active?: boolean
  display_name?: string
  search_terms?: string[]
  exclude_terms?: string[]
  sort_order?: number
}

export interface SportsChannelMapping {
  id: string
  team_id: string
  stream_id: string
  enabled: boolean
  epg_channel_number?: number
  custom_channel_name?: string
  created_at: string
  updated_at: string
  team?: SportsTeam
  stream?: { id: string; name: string }
}

export interface CreateSportsChannelMappingRequest {
  team_id: string
  stream_id: string
  epg_channel_number?: number
  custom_channel_name?: string
}

export interface UpdateSportsChannelMappingRequest {
  stream_id?: string
  enabled?: boolean
  epg_channel_number?: number
  custom_channel_name?: string
}

export interface SportsGame {
  id: string
  team_id: string
  league_id: string
  game_date: string
  espn_game_id?: string
  opponent_name?: string
  opponent_abbr?: string
  is_home: boolean
  game_time?: string
  game_end_time?: string
  venue?: string
  broadcast: string[]
  home_score: number
  away_score: number
  period?: string
  clock?: string
  possession?: string
  is_red_zone: boolean
  game_status: string
  status_detail?: string
  panel_stream_id?: string
  panel_stream_url?: string
  matched_at?: string
  match_confidence?: number
  created_at: string
  updated_at: string
}

export interface SportsStatus {
  running: boolean
  last_update?: string
  next_update?: string
  active_games: number
  enabled_leagues: number
  total_mapped_channels: number
  league_statuses: LeagueStatus[]
  recent_errors?: string[]
  emby_connected: boolean
  panel_connected: boolean
  panel_sources?: PanelSourceStatus[]
}

export interface LeagueStatus {
  code: string
  name: string
  enabled: boolean
  last_epg_generation?: string
  last_score_update?: string
  active_games: number
  // Team-based leagues
  team_count: number
  mapped_teams: number
  // Generic channel leagues (NFL)
  use_generic_channels: boolean
  channel_count: number
  mapped_channels: number
}

export interface SportsEPGRun {
  id: string
  league_id?: string
  run_type: string
  started_at: string
  completed_at?: string
  games_processed: number
  games_matched: number
  errors: number
  status: string
  error_message?: string
}

// Generic Channels (NFL-style)
export interface SportsGenericChannel {
  id: string
  league_id: string
  channel_number: number
  channel_name: string
  panel_stream_id?: string
  panel_stream_name?: string
  stream_id?: string
  stream_name?: string
  enabled: boolean
  sort_order: number
  created_at: string
  updated_at: string
  current_game?: SportsChannelGame
}

export interface SportsChannelGame {
  id: string
  channel_id: string
  league_id: string
  espn_game_id?: string
  game_date: string
  game_time?: string
  game_end_time?: string
  home_team_name?: string
  away_team_name?: string
  home_team_abbr?: string
  away_team_abbr?: string
  home_team_logo?: string
  away_team_logo?: string
  home_score: number
  away_score: number
  game_status: string
  period?: string
  clock?: string
  possession?: string
  is_red_zone: boolean
  down_distance?: string
  venue?: string
  broadcast?: string[]
}

export interface UpdateSportsGenericChannelRequest {
  channel_name?: string
  enabled?: boolean
  stream_id?: string
  panel_stream_id?: string
  panel_stream_name?: string
}

export interface MapGenericChannelRequest {
  stream_id?: string
  panel_stream_id?: string
  panel_stream_name?: string
}

// EPG Sync Status types
export interface EPGSyncStatusResponse {
  sources: EPGSourceSyncStatus[]
}

export interface EPGSourceSyncStatus {
  source_id: string
  source_name: string
  status: 'syncing' | 'completed' | 'error' | 'pending'
  last_sync?: string
  next_sync?: string
  error?: string
  program_count: number
  channel_count: number
  duration_ms: number
  sync_interval_hours: number
  sync_time?: string | null // "HH:MM" UTC if time-based sync
}

// XUI Import types
export interface XUICategory {
  id: string
  name: string
}

export interface XUIStream {
  id: string
  name: string
  category_id: string
  category_name: string
  icon: string
  epg_channel_id: string
  exists: boolean
}

export interface XUIStreamListResponse {
  streams: XUIStream[]
  categories: XUICategory[]
  total: number
}

export interface M3UStream extends XUIStream {
  url: string
}

export interface M3UStreamListResponse {
  streams: M3UStream[]
  categories: XUICategory[]
  total: number
}

export interface CategoryConfig {
  panel_category_id: string
  channel_range_start: number
  channel_range_end: number
  auto_assign_numbers: boolean
}

export interface ImportStreamsRequest {
  stream_ids: string[]
  category_id?: string
  create_category?: boolean
  category_configs?: CategoryConfig[]
  panel_url?: string
  panel_username?: string
  panel_password?: string
}

export interface ImportM3UStreamsRequest {
  file: File
  stream_ids: string[]
  category_id?: string
  create_category?: boolean
}

export interface ImportStreamsResponse {
  imported: number
  skipped: number
  errors?: string[]
}

// Broadcast Message types (Emby notifications)
export interface BroadcastConfig {
  emby_url: string
  emby_api_key?: string
  has_api_key: boolean
}

export interface BroadcastConfigRequest {
  emby_url: string
  emby_api_key?: string
}

export interface BroadcastMessage {
  id: string
  text: string
  timeout_ms?: number
  sent_at: string
  sessions_count?: number
  target_stream_id?: string
  severity?: string
  placement?: string
  display_type?: string
  duration_seconds?: number
}

export interface BroadcastSendRequest {
  text: string
  timeout_ms?: number
  target_stream_id?: string
  severity?: string
  placement?: string
  display_type?: string
  duration_seconds?: number
}

export interface BroadcastSendResponse {
  status: string
  sessions_total: number
  sessions_success: number
  message: BroadcastMessage
}

export interface BroadcastTestResponse {
  status: string
  sessions_count: number
  sessions: EmbySession[]
}

export interface EmbySession {
  Id: string
  DeviceId: string
  DeviceName: string
  UserName: string
  Client: string
}

export interface AssignChannelNumbersResponse {
  message: string
  assigned: number
  skipped: number
  categories: Array<{
    category: string
    assigned: number
    skipped: number
  }>
}

// Panel Source types (multi-source sports EPG)
export interface SportsPanelSource {
  id: string
  name: string
  url: string
  username: string
  password: string
  category_filter?: string
  is_default: boolean
  enabled: boolean
  last_connected_at?: string
  last_error?: string
  stream_count: number
  created_at: string
  updated_at: string
}

export interface CreatePanelSourceRequest {
  name: string
  url: string
  username: string
  password: string
  category_filter?: string
  is_default?: boolean
}

export interface UpdatePanelSourceRequest {
  name?: string
  url?: string
  username?: string
  password?: string
  category_filter?: string
  enabled?: boolean
}

export interface PanelSourceTestResult {
  success: boolean
  message?: string
  error?: string
}

export interface PanelSourceStatus {
  id: string
  name: string
  connected: boolean
  last_check?: string
  stream_count: number
  consecutive_failures: number
}
